import { axiosAiq } from 'config';
import { call, put, select } from 'redux-saga/effects';
import { AUTOMATION_SOURCE } from 'utils/constants';
import { notificationHandler } from 'utils/utils';
import { selectTransactionsSplitViewMode } from 'store/transactionsSlice/transactionsSelectors';
import { selectFormById } from '../../formControlSelectors';
import { formControlActions } from '../../formControlSlice';
import { buildPartialAutomation, buildPartialFieldMappings } from '../../lib/copyToFormHelpers';
import { beginCellCopy, beginCopy, dropIfStale, endCopy } from '../../lib/copyRequestGuard';

const ANNOUNCEMENT_CELL_TO_FORM_FIELD_MAP = Object.freeze({
    // Vehicle
    truckNumber: 'vehicle.unitNumber',
    truckUnitNumber: 'vehicle.unitNumber',
    unitNumber: 'vehicle.unitNumber',
    licencePlate: 'vehicle.licensePlateNumber',
    licensePlate: 'vehicle.licensePlateNumber',
    licensePlateNumber: 'vehicle.licensePlateNumber',
    transportType: 'vehicle.transportType',
    // Trailer
    trailerNumber: 'trailer.trailerNumber',
    trailerTwoNumber: 'trailer.trailerNumberTwo',
    trailerNumberTwo: 'trailer.trailerNumberTwo',
    // Cargo
    moveNumber: 'cargo.moveNumber',
    cargoMoveNumber: 'cargo.moveNumber',
    sealNumber: 'cargo.sealNumber',
    cargoSealNumber: 'cargo.sealNumber',
    routeNumber: 'cargo.routeNumber',
    // Person / company
    fullName: 'person.fullName',
    driverName: 'person.fullName',
    companyName: 'person.companyName',
    identificationNumber: 'person.identificationNumber',
    identificationType: 'person.identificationType',
    phoneNumber: 'person.phoneNumber',
    companyCardNumber: 'person.companyCardNumber',
    dotNumber: 'person.dotNumber',
    scac: 'person.scac',
    // Misc
    direction: 'gateDirection',
    gateDirection: 'gateDirection',
    'gate.direction': 'gateDirection',
    locationName: 'gate.location.fullName',
    description: 'description',
});

export function* copyLocationTransactionHandler({ payload: { transactionId, announcementsMode, copy, tableName: table, key } }) {
    const copySource = copy ? { tableName: table, key } : null;
    const copyId = yield call(beginCopy, transactionId, copySource);
    // No open form to copy into.
    if (copyId == null) return;

    try {
        if (copy) {
            try {
                const urlPath = `/transactions/trucks${announcementsMode ? '/announcements' : ''}/from/${key}/to/${transactionId}`;
                const { data: transactionData } = yield axiosAiq.get(urlPath);
                if (yield call(dropIfStale, transactionId, copyId, copySource)) return;
                yield put(formControlActions.setTransactionData({ transactionData }));
            } catch (err) {
                // A superseded copy fails silently: its result would have been dropped anyway.
                if (yield call(dropIfStale, transactionId, copyId, copySource)) return;
                yield notificationHandler({ err, title: `ERROR COPYING DATA TO FORM` });
                return;
            }
        }

        const manualAnnouncementIdUsed = copy && announcementsMode ? key : '';
        const tableName = copy ? table : '';
        const isSplitViewMode = yield select(selectTransactionsSplitViewMode);

        const automationPayload = copy
            ? {
                  transactionId,
                  automationObjTemplate: {
                      source: announcementsMode ? AUTOMATION_SOURCE.MANUAL_ANNOUNCEMENT : AUTOMATION_SOURCE.HISTORICAL_TRANSACTION,
                  },
                  isSplitViewMode,
              }
            : { transactionId, setAlisaAutomation: true, isSplitViewMode };

        yield put(formControlActions.setAutomationValues(automationPayload));
        yield put(formControlActions.setAnnouncementIdUsed({ formId: transactionId, manualAnnouncementIdUsed }));
        yield put(formControlActions.setActiveSelectionTableName({ formId: transactionId, tableName }));
        yield put(formControlActions.validateTransactionWithInventoryData({ formId: transactionId }));
    } finally {
        yield call(endCopy, transactionId, copyId);
    }
}

/**
 * `copySourceTable` names the table the row comes from for the latest-copy guard only, when it differs from
 * `tableName` (an appointment cell copy sends no `tableName`, so it does not change the active selection).
 */
export function* copyLocationTransactionPartialHandler({
    payload: { transactionId, tableName: table, copySourceTable = table, announcementsMode = false, rowData, cells, _sourceOverride },
}) {
    if (!Array.isArray(cells) || cells.length === 0 || !rowData) return;

    const form = yield select(selectFormById(transactionId));
    if (!form?.formConfig?.fields?.length) return;

    const formFields = form.formConfig.fields;

    const cellToFormFieldMap = announcementsMode ? ANNOUNCEMENT_CELL_TO_FORM_FIELD_MAP : null;
    const fieldMappingsMap = buildPartialFieldMappings(cells, formFields, cellToFormFieldMap);
    if (Object.keys(fieldMappingsMap).length === 0) return;

    const source = _sourceOverride || (announcementsMode ? AUTOMATION_SOURCE.MANUAL_ANNOUNCEMENT : AUTOMATION_SOURCE.HISTORICAL_TRANSACTION);
    const partialAutomation = buildPartialAutomation({ fieldMappings: fieldMappingsMap, rowData, source });
    if (Object.keys(partialAutomation).length === 0) return;

    const isSplitViewMode = yield select(selectTransactionsSplitViewMode);

    // The newest selection: a full-row copy still loading must not land over it, unless it is for this row.
    const copyId = yield call(beginCellCopy, transactionId, { tableName: copySourceTable, key: rowData.id });
    try {
        yield put(
            formControlActions.setAutomationValues({
                transactionId,
                // Only the copied cells; the reducer merges them into the current automation.
                automationUpdates: partialAutomation,
                isSplitViewMode,
            })
        );

        if (table) {
            yield put(formControlActions.setActiveSelectionTableName({ formId: transactionId, tableName: table }));
        }

        yield put(formControlActions.validateTransactionWithInventoryData({ formId: transactionId }));
    } finally {
        yield call(endCopy, transactionId, copyId);
    }
}
